/**
 * "Save to a Project" (snippets/project-picker.liquid): the popup a save button
 * opens, so a product goes into a project the shopper picks rather than into
 * one default list.
 *
 * Talks to Enlist only through window.qfl.enlist (the Advanced route):
 *   lists()                  the shopper's projects
 *   has({ variantId, list }) whether one already holds this product
 *   createList(name)         "Create New Project"
 *   add({ variantId, productId, list })
 *
 * assets/wishlist-enlist.js calls window.ukfdProjectPicker.open() from its save
 * button handler; everything else happens here.
 */

const ROOT = "[data-project-picker]";
const LAST_KEY = "ukfd.projectPicker.last";

const enlist = () => (window.qfl && window.qfl.enlist) || window.QFL || null;
const readLast = () => {
  try {
    return localStorage.getItem(LAST_KEY);
  } catch {
    return null;
  }
};
const writeLast = (t) => {
  try {
    localStorage.setItem(LAST_KEY, t);
  } catch {
    /* storage off */
  }
};

function projects(root) {
  const q = enlist();
  if (!q) return [];
  const hidden = (root.dataset.hideListNamed || "").trim().toLowerCase();
  let lists = [];
  try {
    lists = q.lists() || [];
  } catch {
    return [];
  }
  return (
    lists
      // The pinned list (with pinning on) holds every save and is not a project;
      // a named default list is left out the same way Saved Projects leaves it out.
      .filter((l) => !l.is_primary)
      .filter((l) => !hidden || (l.name || "").trim().toLowerCase() !== hidden)
      .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))
  );
}

let current = null; // { productId, variantId, trigger, covered }

// Opened from a card in the side cart's recommendations: that modal is a
// .modal-overlay at z-index 999999 (utilities.css), above the picker, so it is
// hidden while the picker is up and shown again when the picker closes. Hidden
// rather than closed, so the recommendations keep their place and the modal's
// "dismissed" state is not touched.
const COVERED_CLASS = "is-behind-project-picker";
const RECS_MODAL = '[data-modal="cart-recommendations"]';

function render(root) {
  const q = enlist();
  const host = root.querySelector("[data-project-picker-options]");
  const tpl = root.querySelector("template[data-project-picker-option]");
  const list = projects(root);

  // Preselect: a project that already holds this product, else the one used
  // last, else the newest.
  const holding = list.find((l) => {
    try {
      return q.has({ variantId: current.variantId, list: l.share_token });
    } catch {
      return false;
    }
  });
  const last = list.find((l) => l.share_token === readLast());
  const selected = (holding || last || list[0] || {}).share_token;

  host.replaceChildren(
    ...list.map((l) => {
      const node = tpl.content.firstElementChild.cloneNode(true);
      const radio = node.querySelector("[data-project-picker-radio]");
      radio.value = l.share_token;
      radio.checked = l.share_token === selected;
      node.querySelector("[data-project-picker-name]").textContent =
        (l.name || "").trim() || root.dataset.defaultName;
      return node;
    }),
  );

  // No projects yet: straight to naming the first one.
  const newBox = root.querySelector("[data-project-picker-new]");
  const createBtn = root.querySelector("[data-project-picker-create]");
  newBox.hidden = list.length > 0;
  createBtn.hidden = list.length === 0;
  root.querySelector("[data-project-picker-new-name]").value = "";
  setError(root, "");
  syncButton(root);
}

function selectedToken(root) {
  return root.querySelector("[data-project-picker-radio]:checked")?.value || null;
}

function creating(root) {
  return !root.querySelector("[data-project-picker-new]").hidden;
}

// "Already in this project" when the chosen project holds the product.
function syncButton(root) {
  const btn = root.querySelector("[data-project-picker-add]");
  if (!btn.dataset.label) btn.dataset.label = btn.textContent.trim();
  const token = creating(root) ? null : selectedToken(root);
  let held = false;
  try {
    held = !!token && enlist().has({ variantId: current.variantId, list: token });
  } catch {
    /* not ready */
  }
  btn.textContent = held ? root.dataset.alreadyLabel : btn.dataset.label;
  btn.disabled = held;
}

function setError(root, text) {
  const el = root.querySelector("[data-project-picker-error]");
  el.textContent = text;
  el.hidden = !text;
}

function open({ productId, variantId, trigger } = {}) {
  const root = document.querySelector(ROOT);
  if (!root || !enlist() || !variantId) return false;
  current?.covered?.classList.remove(COVERED_CLASS);
  const covered = trigger?.closest?.(RECS_MODAL) || null;
  covered?.classList.add(COVERED_CLASS);
  current = { productId, variantId, trigger, covered };
  // The lists page the merchant set in Enlist, if there is one.
  try {
    const path = enlist().settings?.().lists_page_path;
    if (path) root.querySelector("[data-project-picker-view-all]").setAttribute("href", path);
  } catch {
    /* keep the theme default */
  }
  render(root);
  root.hidden = false;
  document.documentElement.classList.add("project-picker-open");
  (
    root.querySelector("[data-project-picker-radio]:checked") ||
    root.querySelector("[data-project-picker-new-name]:not([hidden])") ||
    root.querySelector(".project-picker__card")
  ).focus();
  return true;
}

function close() {
  const root = document.querySelector(ROOT);
  if (!root || root.hidden) return;
  root.hidden = true;
  document.documentElement.classList.remove("project-picker-open");
  // Shown again before focus goes back: the trigger is inside it.
  current?.covered?.classList.remove(COVERED_CLASS);
  current?.trigger?.focus?.();
  current = null;
}

async function save(root) {
  const q = enlist();
  const btn = root.querySelector("[data-project-picker-add]");
  if (btn.disabled || btn.getAttribute("aria-busy") === "true") return;
  btn.setAttribute("aria-busy", "true");
  setError(root, "");
  try {
    let token = creating(root) ? null : selectedToken(root);
    if (!token) {
      const name =
        root.querySelector("[data-project-picker-new-name]").value.trim() ||
        root.dataset.defaultName;
      const made = await q.createList(name);
      if (!made?.ok || !made.share_token) throw new Error("create");
      token = made.share_token;
    }
    const res = await q.add({
      variantId: current.variantId,
      productId: current.productId,
      list: token,
    });
    if (!res?.ok) throw new Error("add");
    writeLast(token);
    btn.textContent = root.dataset.addedLabel;
    btn.disabled = true;
    setTimeout(close, 1000);
  } catch {
    setError(root, root.dataset.errorLabel);
  } finally {
    btn.removeAttribute("aria-busy");
  }
}

function wire() {
  const root = document.querySelector(ROOT);
  if (!root || root.dataset.bound === "true") return;
  root.dataset.bound = "true";
  root.addEventListener("click", (e) => {
    if (e.target.closest("[data-project-picker-close]")) {
      e.preventDefault();
      close();
      return;
    }
    if (e.target.closest("[data-project-picker-create]")) {
      root.querySelector("[data-project-picker-new]").hidden = false;
      root.querySelector("[data-project-picker-create]").hidden = true;
      root.querySelectorAll("[data-project-picker-radio]").forEach((r) => {
        r.checked = false;
      });
      root.querySelector("[data-project-picker-new-name]").focus();
      syncButton(root);
    }
  });
  root.addEventListener("change", (e) => {
    if (!e.target.matches("[data-project-picker-radio]")) return;
    // Picking an existing project leaves "create" behind.
    if (root.querySelector("[data-project-picker-options]").children.length) {
      root.querySelector("[data-project-picker-new]").hidden = true;
      root.querySelector("[data-project-picker-create]").hidden = false;
    }
    syncButton(root);
  });
  root.querySelector("[data-project-picker-form]").addEventListener("submit", (e) => {
    e.preventDefault();
    save(root);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !root.hidden) close();
  });
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", wire);
else wire();

window.ukfdProjectPicker = { open, close };
